import { useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ProductWarehouseStock } from '@/features/warehouse/product-stock-panel'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import {
  BarChart3,
  Box,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  ListTree,
  Package,
  Settings,
  ShoppingCart,
  Truck,
} from 'lucide-react'
import { useProduct, useProductOptions, useProductPricingDetail } from '@/api/hooks'
import { cn, formatMoney, formatPercent, parseUTCDate } from '@/lib/utils'
import { BackButton } from '@/components/back-button'
import { ProductPriceHistoryCard } from '@/components/product-price-history'
import { PricingDetailView } from '@/components/pricing-detail'
import { BulkPricingDetail } from '@/components/bulk-pricing-detail'
import { ProductMapNetCard } from '@/components/product-map-net-card'
import { ProductConfigV4Section } from '@/components/products/product-config-v4-section'
import { ProductOptionsSection } from '@/components/products/product-options-section'
import { ProductRecommendationShowcase } from '@/components/products/product-recommendation-showcase'
import { ProductDiscontinuationPanel } from '@/components/products/product-discontinuation-panel'
import { ProductShippingCard } from '@/components/products/product-shipping-card'
import { ShopifyPriceStatus } from '@/components/products/shopify-price-status'
import { ProductStoreReadiness } from '@/features/store-readiness/product-store-readiness'
import { ProductFactsSection } from '@/components/products/product-facts-section'
import { ProductCorrectionsSection } from '@/components/products/product-corrections-section'
import { ProductSpecTable } from '@/components/products/product-spec-table'
import { ProductModel3DPanel } from '@/components/products/product-model-3d'
import {
  MediaHideButton,
  MediaRestoreControl,
  MediaUploadButton,
  ProductImagesPanel,
} from '@/components/products/product-media-corrections'
import { mediaIdOf, useStaffHidden } from '@/api/product-corrections'
import { SettingCell } from '@/components/setting-cell'
import type { components } from '@/api/schema'
import { shopifyAdminProductUrl, storefrontProductUrl } from '@/lib/shopify-product-links'
import { usePageTitle } from '@/lib/page-title'

type AttributeValue = components['schemas']['ProductAttributeValueRead']
type ManufacturerDiscount = components['schemas']['ManufacturerDiscountRead']

function displayCategoryPropertyName(name: string | null | undefined) {
  if (!name) return '-'
  return name.trim().toUpperCase() === 'CATEGORY' ? 'Product Type' : name
}

function humanizeToken(value: string | null | undefined) {
  if (!value) return '-'
  const labels: Record<string, string> = {
    aq: 'AutoQuotes',
    auto_min_margin: 'Margin-adjusted',
    auto_quotes_api: 'AutoQuotes',
    ea: 'Each',
    manual: 'Manual price',
    minimum_margin: 'Margin-adjusted',
    pricing_terms: 'Reviewed pricing',
  }
  const normalized = value.trim().toLowerCase()
  if (labels[normalized]) return labels[normalized]
  return value
    .replaceAll('_', ' ')
    .replaceAll('-', ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase())
}

/** Where the effective Net came from, when the discount chain did not set it. */
function netSourceLabel(source: string | null | undefined) {
  const labels: Record<string, string> = {
    manual: 'Special NET',
    manufacturer_special: 'manufacturer special',
    net_item: 'AutoQuotes NET item',
  }
  return labels[source?.trim().toLowerCase() ?? ''] ?? humanizeToken(source)
}

/** The Price card's discount: the chain, its name, and what set Net when the chain did not. */
export function PriceDiscountCell({
  discount,
  netSource,
}: {
  discount: ManufacturerDiscount | null | undefined
  netSource: string | null | undefined
}) {
  // A linked chain that did not set Net (Special NET, a special, an AQ NET item) says so.
  const showNetSource = !!netSource && netSource !== 'discount_chain'
  return (
    <SettingCell
      label="Discount"
      below={
        discount && (discount.name || showNetSource) ? (
          <div className="mt-0.5 space-y-0.5 text-xs text-muted-foreground">
            {discount.name && <p className="break-words">{discount.name}</p>}
            {showNetSource && <p>Net from {netSourceLabel(netSource)}</p>}
          </div>
        ) : null
      }
    >
      <span className="text-sm font-medium tabular-nums">{discount?.discount || '-'}</span>
    </SettingCell>
  )
}

/** "Each", or "Case · priced each" when the selling unit and the pricing unit differ. */
function soldBy(sellingUnit: string | null | undefined, priceBy: string | null | undefined) {
  const selling = humanizeToken(sellingUnit)
  const pricing = humanizeToken(priceBy)
  if (!sellingUnit) return pricing
  if (!priceBy || selling.toLowerCase() === pricing.toLowerCase()) return selling
  return `${selling} · priced ${pricing.toLowerCase()}`
}

interface ProductDetailPageProps {
  productId: string
}

export function ProductDetailPage({ productId }: ProductDetailPageProps) {
  const [tab, setTab] = useState('shipping')
  const { data: product, isLoading, isError, refetch } = useProduct(productId)
  usePageTitle(product?.model)
  const { data: pricingDetail, isLoading: pricingDetailLoading } =
    useProductPricingDetail(productId)
  const { data: productOptions } = useProductOptions(productId)
  const staffHidden = useStaffHidden(productId)

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-6 md:grid-cols-3">
          <Skeleton className="h-64 md:col-span-1" />
          <Skeleton className="h-64 md:col-span-2" />
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="mx-auto max-w-7xl space-y-3">
        <p className="text-sm text-destructive">Could not load this product.</p>
        <Button variant="outline" onClick={() => void refetch()}>
          Try again
        </Button>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="mx-auto max-w-7xl">
        <p className="text-muted-foreground">Product not found</p>
      </div>
    )
  }

  const visibleImages =
    product.images?.filter((i) => !i.hidden && (i.public_url || i.store_url)) ?? []

  const margin =
    product.current_price?.store_price && product.current_price?.net_price
      ? ((Number(product.current_price.store_price) - Number(product.current_price.net_price)) /
          Number(product.current_price.store_price)) *
        100
      : null

  const profit =
    product.current_price?.store_price && product.current_price?.net_price
      ? Number(product.current_price.store_price) - Number(product.current_price.net_price)
      : null

  // Group attribute values by their group name
  const attributesByGroup: Record<string, AttributeValue[]> = {}
  for (const av of product.attribute_values ?? []) {
    const groupName = av.attribute_definition?.group?.name ?? 'Other'
    if (!attributesByGroup[groupName]) attributesByGroup[groupName] = []
    attributesByGroup[groupName].push(av)
  }

  function formatAttributeValue(av: AttributeValue) {
    const def = av.attribute_definition
    const unit = av.unit || def?.unit || ''
    if (av.number_value != null) return `${av.number_value}${unit ? ` ${unit}` : ''}`
    if (av.text_value != null) return av.text_value
    if (av.bool_value != null) return av.bool_value ? 'Yes' : 'No'
    if (av.range_min != null || av.range_max != null)
      return `${av.range_min ?? '?'} – ${av.range_max ?? '?'}${unit ? ` ${unit}` : ''}`
    if (av.enum_value != null) return av.enum_value
    return '-'
  }

  const hasConfigurations =
    product.configuration_groups?.some((g) => !g.hidden) ||
    product.accessory_groups?.some((g) => !g.hidden)
  const showConfigurations = product.configurable || hasConfigurations || !!productOptions
  const hasPriceHistory = (product.prices?.length ?? 0) > 1
  const storefrontUrl = storefrontProductUrl(product.shopify_handle)
  const shopifyAdminUrl = shopifyAdminProductUrl(product.shopify_product_id)
  const tariffLabel = product.manufacturer.tariff_label?.trim() || 'Tariff'
  const price = product.current_price

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-24">
      <header>
        <BackButton fallback={{ to: '/products' }} fallbackLabel="Back to Products" />
        <div className="mt-2 flex items-start gap-3 sm:gap-4">
          <button
            type="button"
            onClick={() => setTab('images')}
            aria-label="Show images"
            className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-white transition-shadow hover:shadow-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none sm:size-20"
          >
            {visibleImages[0] ? (
              <img
                src={visibleImages[0].public_url || visibleImages[0].store_url || ''}
                alt=""
                className="size-full object-contain p-1"
              />
            ) : (
              <Package className="h-7 w-7 text-muted-foreground/30" />
            )}
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <h1 className="break-all text-2xl font-bold tracking-tight">{product.model}</h1>
              <div className="flex flex-wrap gap-1.5">
                {product.shopify_product_id && <Badge variant="default">Shopify</Badge>}
                {product.discontinued_at && <Badge variant="destructive">Discontinued</Badge>}
                {product.configurable && <Badge variant="outline">Configurable</Badge>}
              </div>
            </div>
            {product.store_title && (
              <p className="mt-0.5 line-clamp-2 max-w-3xl text-sm">{product.store_title}</p>
            )}
            <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              {product.manufacturer?.uuid ? (
                <Link
                  to="/manufacturers/$manuId"
                  params={{ manuId: product.manufacturer.uuid }}
                  className="font-medium text-primary hover:underline"
                >
                  {product.manufacturer_name}
                </Link>
              ) : (
                <span className="font-medium">{product.manufacturer_name}</span>
              )}
              {product.brand?.name && product.brand.name !== product.manufacturer_name && (
                <span>{product.brand.name}</span>
              )}
              {storefrontUrl && (
                // eslint-disable-next-line no-restricted-syntax -- external URL
                <a
                  href={storefrontUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  Storefront <ExternalLink className="h-3 w-3" />
                </a>
              )}
              {shopifyAdminUrl && (
                // eslint-disable-next-line no-restricted-syntax -- external URL
                <a
                  href={shopifyAdminUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  Shopify admin <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </div>
        </div>
      </header>

      <ProductStoreReadiness productUuid={product.uuid} />

      <section
        aria-labelledby="product-price"
        className="overflow-hidden rounded-xl border bg-card"
      >
        <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
          <h2 id="product-price" className="text-sm font-semibold">
            Price
          </h2>
          <ShopifyPriceStatus shopifyPrice={product.shopify_price} />
        </div>
        {price ? (
          <div className="-mb-px -mr-px grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
            <SettingCell
              label="Store price"
              below={
                pricingDetail?.quantity_pricing && (
                  <p className="text-xs text-muted-foreground tabular-nums">
                    Qty 1: {formatMoney(pricingDetail.quantity_pricing.quantity_one_price)} each
                  </p>
                )
              }
            >
              <span className="text-lg font-semibold tabular-nums">
                {formatMoney(price.store_price)}
              </span>
            </SettingCell>
            <SettingCell label="Net">
              <span className="text-lg font-semibold tabular-nums text-muted-foreground">
                {formatMoney(price.net_price)}
              </span>
            </SettingCell>
            <SettingCell label="Profit">
              <span className="text-lg font-semibold tabular-nums text-green-700 dark:text-green-500">
                {formatMoney(profit)}
              </span>
            </SettingCell>
            <SettingCell label="Margin">
              <span className="text-lg font-semibold tabular-nums text-green-700 dark:text-green-500">
                {formatPercent(margin)}
              </span>
            </SettingCell>
            <SettingCell label="List price">
              <span className="text-sm font-medium tabular-nums">
                {formatMoney(price.list_price)}
              </span>
            </SettingCell>
            <SettingCell label="MAP">
              <span className="text-sm font-medium tabular-nums">
                {formatMoney(price.map_price)}
              </span>
            </SettingCell>
            <SettingCell label="Base NET">
              <span className="text-sm font-medium tabular-nums">
                {formatMoney(price.base_net_price)}
              </span>
            </SettingCell>
            <PriceDiscountCell discount={product.manufacturer_discount} netSource={price.net_source} />
            <SettingCell label={tariffLabel}>
              <span className="text-sm font-medium tabular-nums">
                {price.manufacturer_tariff_percentage == null
                  ? '-'
                  : `${formatPercent(price.manufacturer_tariff_percentage)} · ${formatMoney(price.tariff_amount)}`}
              </span>
            </SettingCell>
            {price.markup != null && (
              <SettingCell label="Markup">
                <span className="text-sm font-medium tabular-nums">
                  {formatPercent(price.markup)}
                </span>
              </SettingCell>
            )}
            <SettingCell label="Price source">
              <span className="text-sm font-medium">{humanizeToken(price.source)}</span>
            </SettingCell>
          </div>
        ) : (
          <p className="px-4 py-6 text-sm text-muted-foreground">No pricing available</p>
        )}
      </section>

      <ProductWarehouseStock productUuid={product.uuid} />

      <ProductFactsSection
        productId={productId}
        afterWeight={
          <SettingCell label="Lead time">
            <span
              className={cn(
                'text-sm font-medium',
                product.lead_time_days == null && 'font-normal text-muted-foreground',
              )}
            >
              {product.lead_time_days != null ? `${product.lead_time_days} days` : 'Not set'}
            </span>
          </SettingCell>
        }
        afterGtins={
          <>
            <SettingCell label="Sold by">
              <span className="text-sm font-medium">
                {soldBy(product.selling_unit, product.price_by)}
              </span>
            </SettingCell>
            {product.external_upc && (
              <SettingCell label="UPC">
                <span className="font-mono text-xs">{product.external_upc}</span>
              </SettingCell>
            )}
            {product.external_gtin && (
              <SettingCell label="External GTIN">
                <span className="font-mono text-xs">{product.external_gtin}</span>
              </SettingCell>
            )}
          </>
        }
      />

      <ProductCorrectionsSection
        productId={productId}
        fallbackTitle={product.store_title}
        fallbackSpecifications={product.specifications}
      />

      <Tabs value={tab} onValueChange={setTab} className="pt-2">
        <TabsList variant="line" className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="shipping">
            <Truck className="h-4 w-4" />
            Shipping
          </TabsTrigger>
          <TabsTrigger value="specs">
            <ListTree className="h-4 w-4" />
            Specs
          </TabsTrigger>
          {showConfigurations && (
            <TabsTrigger value="configurations">
              <Settings className="h-4 w-4" />
              Configurations
            </TabsTrigger>
          )}
          <TabsTrigger value="images">
            <ImageIcon className="h-4 w-4" />
            Images ({visibleImages.length})
          </TabsTrigger>
          <TabsTrigger value="model-3d">
            <Box className="h-4 w-4" />
            3D model
          </TabsTrigger>
          <TabsTrigger value="documents">
            <FileText className="h-4 w-4" />
            Documents
          </TabsTrigger>
          <TabsTrigger value="pricing">
            <BarChart3 className="h-4 w-4" />
            Pricing
          </TabsTrigger>
          <TabsTrigger value="shopify">
            <ShoppingCart className="h-4 w-4" />
            Shopify
          </TabsTrigger>
        </TabsList>

        <TabsContent value="shipping" className="mt-4 space-y-4">
          <ProductShippingCard
            productId={productId}
            manufacturerName={product.manufacturer_name}
            freeShipping={product.shipping_free}
          />
        </TabsContent>

        <TabsContent value="specs" className="mt-4 space-y-4">
          <ProductSpecTable productId={productId} />
          {((product.categories?.length ?? 0) > 0 ||
            (product.certifications?.length ?? 0) > 0) && (
            <section className="space-y-3 rounded-xl border bg-card px-4 py-3">
              {product.categories?.length > 0 && (
                <div>
                  <h2 className="mb-2 text-sm font-semibold">Categories</h2>
                  <div className="flex flex-wrap gap-1.5">
                    {product.categories.map((cat) => (
                      <Badge
                        key={cat.uuid}
                        variant="secondary"
                        className="max-w-full whitespace-normal break-words text-left"
                      >
                        {(cat.path_name || cat.name).split(/\s*;\s*|\s*>\s*/).join(' › ')}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {product.certifications?.length > 0 && (
                <div>
                  <h2 className="mb-2 text-sm font-semibold">Certifications</h2>
                  <div className="flex flex-wrap gap-1.5">
                    {product.certifications.map((cert) => (
                      <Badge key={cert.uuid} variant="outline">
                        {cert.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}
          {product.category_property_values?.length > 0 && (
            <section
              aria-labelledby="product-properties"
              className="overflow-hidden rounded-xl border bg-card"
            >
              <h2 id="product-properties" className="border-b px-4 py-3 text-sm font-semibold">
                Category properties
              </h2>
              <div className="-mb-px -mr-px grid grid-cols-2 lg:grid-cols-4">
                {product.category_property_values.map((cpv) => (
                  <SettingCell
                    key={cpv.uuid}
                    label={displayCategoryPropertyName(cpv.property?.name)}
                  >
                    <span className="text-sm font-medium">{cpv.value}</span>
                  </SettingCell>
                ))}
              </div>
            </section>
          )}
          {Object.entries(attributesByGroup).map(([groupName, values]) => (
            <section
              key={groupName}
              aria-label={groupName}
              className="overflow-hidden rounded-xl border bg-card"
            >
              <h2 className="border-b px-4 py-3 text-sm font-semibold">{groupName}</h2>
              <div className="-mb-px -mr-px grid grid-cols-2 lg:grid-cols-4">
                {values.map((av) => (
                  <SettingCell key={av.uuid} label={av.attribute_definition?.name || '-'}>
                    <span className="text-sm font-medium">{formatAttributeValue(av)}</span>
                  </SettingCell>
                ))}
              </div>
            </section>
          ))}
        </TabsContent>

        {showConfigurations && (
          <TabsContent value="configurations" className="mt-4 space-y-4">
            <ProductOptionsSection productId={productId} />
            <ProductConfigV4Section productId={productId} />
            {product.configuration_groups
              ?.filter((g) => !g.hidden)
              .sort((a, b) => a.order - b.order)
              .map((group) => (
                <OptionGroupSection
                  key={group.uuid}
                  title={group.name}
                  description={group.description}
                  rows={(group.options ?? [])
                    .filter((o) => !o.hidden)
                    .sort((a, b) => a.order - b.order)
                    .map((opt) => ({
                      key: opt.uuid,
                      name: opt.name,
                      detail: [opt.description, opt.specifications].filter(Boolean).join(' · '),
                      tag: opt.is_default ? 'Default' : null,
                    }))}
                />
              ))}
            {product.accessory_groups
              ?.filter((g) => !g.hidden)
              .sort((a, b) => a.order - b.order)
              .map((group) => (
                <OptionGroupSection
                  key={group.uuid}
                  title={group.name}
                  description={group.description}
                  badge="Accessory"
                  rows={(group.accessories ?? [])
                    .filter((a) => !a.hidden)
                    .sort((a, b) => a.order - b.order)
                    .map((acc) => ({
                      key: acc.uuid,
                      name: acc.name,
                      model: acc.model,
                      detail: acc.description ?? '',
                      tag: null,
                    }))}
                />
              ))}
          </TabsContent>
        )}

        <TabsContent value="images" className="mt-4">
          <ProductImagesPanel
            productId={productId}
            images={product.images ?? []}
            alt={product.model}
          />
        </TabsContent>

        <TabsContent value="model-3d" className="mt-4">
          <ProductModel3DPanel
            productId={productId}
            model={product.model}
            width={product.width}
            depth={product.depth}
            height={product.height}
          />
        </TabsContent>

        <TabsContent value="documents" className="mt-4 space-y-4">
          {(product.current_description ||
            product.html_description ||
            product.seo_description) && (
            <section className="rounded-xl border bg-card px-4 py-3">
              <h2 className="text-sm font-semibold">Description</h2>
              {product.current_description ? (
                <div
                  className="prose prose-sm mt-2 max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: product.current_description.content,
                  }}
                />
              ) : product.html_description ? (
                <div
                  className="prose prose-sm mt-2 max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: product.html_description,
                  }}
                />
              ) : (
                <p className="mt-2 text-sm">{product.seo_description}</p>
              )}
            </section>
          )}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <ProductFileCard
              title="Documents"
              fallbackName="Document"
              files={product.documents ?? []}
              otherFiles={product.other_documents ?? []}
              action={<MediaUploadButton productId={productId} kind="document" />}
              fileActions={(file) => {
                const id = mediaIdOf(file)
                if (id == null) return null
                const name = file.name || 'Document'
                return (
                  <>
                    <MediaUploadButton
                      productId={productId}
                      kind="document"
                      replaces={{ id, name }}
                    />
                    <MediaHideButton
                      productId={productId}
                      kind="document"
                      mediaId={id}
                      name={name}
                    />
                  </>
                )
              }}
              otherFileStatus={(file) => {
                const correction = staffHidden('document', file)
                return correction ? (
                  <MediaRestoreControl
                    productId={productId}
                    kind="document"
                    correction={correction}
                  />
                ) : null
              }}
            />
            <ProductFileCard
              title="Drawings"
              fallbackName="Drawing"
              files={product.drawings ?? []}
              otherFiles={product.other_drawings ?? []}
            />
          </div>
        </TabsContent>

        <TabsContent value="pricing" className="mt-4 space-y-4">
          <PricingDetailView detail={pricingDetail} isLoading={pricingDetailLoading} />
          <BulkPricingDetail pricing={pricingDetail?.quantity_pricing} />
          <ProductMapNetCard product={product} />
          {product.manufacturer_discount && (
            <section
              aria-labelledby="product-discount"
              className="overflow-hidden rounded-xl border bg-card"
            >
              <h2 id="product-discount" className="border-b px-4 py-3 text-sm font-semibold">
                Manufacturer discount
              </h2>
              <div className="-mb-px -mr-px grid grid-cols-2 lg:grid-cols-4">
                {product.manufacturer_discount.discount && (
                  <SettingCell label="Discount">
                    <span className="text-sm font-medium">
                      {product.manufacturer_discount.discount}
                    </span>
                  </SettingCell>
                )}
                {product.manufacturer_discount.markup && (
                  <SettingCell label="Markup">
                    <span className="text-sm font-medium">
                      {product.manufacturer_discount.markup}
                    </span>
                  </SettingCell>
                )}
                {product.manufacturer_discount.free_freight != null && (
                  <SettingCell label="Free freight">
                    <span className="text-sm font-medium">
                      {['true', 'yes', 'y', '1'].includes(
                        String(product.manufacturer_discount.free_freight).trim().toLowerCase(),
                      )
                        ? 'Yes'
                        : 'No'}
                    </span>
                  </SettingCell>
                )}
                {product.manufacturer_discount.mfr_summary_notes && (
                  <SettingCell label="Notes" className="col-span-full">
                    <span className="text-sm">
                      {product.manufacturer_discount.mfr_summary_notes}
                    </span>
                  </SettingCell>
                )}
                {product.manufacturer_discount.po_instructions && (
                  <SettingCell label="PO instructions" className="col-span-full">
                    <span className="text-sm">{product.manufacturer_discount.po_instructions}</span>
                  </SettingCell>
                )}
              </div>
            </section>
          )}
          {hasPriceHistory && <ProductPriceHistoryCard productUuid={product.uuid} />}
        </TabsContent>

        <TabsContent value="shopify" className="mt-4 space-y-4">
          {product.shopify_product_id ? (
            <>
              <section
                aria-labelledby="product-shopify"
                className="overflow-hidden rounded-xl border bg-card"
              >
                <h2 id="product-shopify" className="border-b px-4 py-3 text-sm font-semibold">
                  Listing
                </h2>
                <div className="-mb-px -mr-px grid grid-cols-2 lg:grid-cols-4">
                  {product.shopify_created_at && (
                    <SettingCell label="Created">
                      <span className="text-sm">
                        {parseUTCDate(product.shopify_created_at).toLocaleString()}
                      </span>
                    </SettingCell>
                  )}
                  {product.shopify_published_at && (
                    <SettingCell label="Published">
                      <span className="text-sm">
                        {parseUTCDate(product.shopify_published_at).toLocaleString()}
                      </span>
                    </SettingCell>
                  )}
                  {product.shopify_updated_at && (
                    <SettingCell label="Updated">
                      <span className="text-sm">
                        {parseUTCDate(product.shopify_updated_at).toLocaleString()}
                      </span>
                    </SettingCell>
                  )}
                  <SettingCell label="Free shipping">
                    <span className="text-sm font-medium">
                      {product.shopify_free_shipping ? 'Yes' : 'No'}
                    </span>
                  </SettingCell>
                </div>
              </section>
              <ProductRecommendationShowcase productId={productId} />
            </>
          ) : null}
          <ProductDiscontinuationPanel productId={productId} model={product.model} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

/** One configuration or accessory group: a table on desktop, stacked rows on a phone. */
function OptionGroupSection({
  title,
  description,
  badge,
  rows,
}: {
  title: string
  description?: string | null
  badge?: string
  rows: Array<{
    key: string
    name: string
    model?: string | null
    detail: string
    tag: string | null
  }>
}) {
  return (
    <section aria-label={title} className="overflow-hidden rounded-xl border bg-card">
      <div className="border-b px-4 py-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          {title}
          {badge && (
            <Badge variant="secondary" className="text-[10px]">
              {badge}
            </Badge>
          )}
        </h2>
        {description && <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>}
      </div>
      <ul className="divide-y">
        {rows.map((row) => (
          <li
            key={row.key}
            className="flex flex-col gap-0.5 px-4 py-2 sm:flex-row sm:items-baseline sm:gap-4"
          >
            <span className="min-w-0 text-sm font-medium sm:w-64 sm:shrink-0">
              {row.name}
              {row.model && (
                <span className="ml-2 font-mono text-xs text-muted-foreground">{row.model}</span>
              )}
            </span>
            <span className="min-w-0 flex-1 break-words text-sm text-muted-foreground">
              {row.detail || '-'}
            </span>
            {row.tag && (
              <Badge variant="default" className="self-start sm:self-auto">
                {row.tag}
              </Badge>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

type ProductFile = {
  uuid?: string | null
  id?: number | null
  name?: string | null
  source_url?: string | null
  hidden?: boolean | null
  error?: string | null
}

function notOnStoreReason(file: ProductFile) {
  if (file.hidden) return 'Hidden'
  if (file.error != null) return 'Download failed'
  return 'Not downloaded'
}

function ProductFileLink({ file, fallbackName }: { file: ProductFile; fallbackName: string }) {
  const name = file.name || fallbackName
  if (!file.source_url) return <span className="block truncate text-sm">{name}</span>
  return (
    // eslint-disable-next-line no-restricted-syntax -- external URL
    <a
      href={file.source_url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex max-w-full items-center gap-1 text-sm text-primary hover:underline"
    >
      <span className="truncate">{name}</span>
      <ExternalLink className="h-3 w-3 shrink-0" />
    </a>
  )
}

export function ProductFileCard({
  title,
  fallbackName,
  files,
  otherFiles,
  action,
  fileActions,
  otherFileStatus,
}: {
  title: string
  fallbackName: string
  files: ProductFile[]
  otherFiles: ProductFile[]
  /** A header button (add a spec sheet); with it the card shows even without files. */
  action?: ReactNode
  /** Buttons at the end of each store file's row (replace, hide). */
  fileActions?: (file: ProductFile) => ReactNode
  /** Replaces the reason a file is not on the store (who hid it, Restore). */
  otherFileStatus?: (file: ProductFile) => ReactNode
}) {
  if (files.length === 0 && otherFiles.length === 0 && !action) return null
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
        {action}
      </CardHeader>
      <CardContent className="space-y-4">
        {files.length === 0 && otherFiles.length === 0 && (
          <p className="text-sm text-muted-foreground">No {title.toLowerCase()}</p>
        )}
        {files.length > 0 && (
          <ul className="space-y-2">
            {files.map((file, index) => (
              <li key={file.uuid ?? index} className="flex items-center gap-2">
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <ProductFileLink file={file} fallbackName={fallbackName} />
                </div>
                {fileActions && (
                  <span className="-my-1 flex shrink-0 items-center">{fileActions(file)}</span>
                )}
              </li>
            ))}
          </ul>
        )}
        {otherFiles.length > 0 && (
          <div className={files.length > 0 ? 'border-t pt-4' : undefined}>
            <p className="mb-2 text-sm font-medium text-muted-foreground">Not on the store</p>
            <ul className="space-y-2">
              {otherFiles.map((file, index) => (
                <li
                  key={file.uuid ?? index}
                  className="flex items-center gap-2 text-muted-foreground"
                >
                  <FileText className="h-4 w-4 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <ProductFileLink file={file} fallbackName={fallbackName} />
                  </div>
                  {otherFileStatus?.(file) ?? (
                    <span className="shrink-0 text-xs">{notOnStoreReason(file)}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
